import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { PropTypes } from 'prop-types';

import { Table } from 'redpoints-front-components/index';
import { paginationToPageable } from 'redpoints-front-common/helpers';

import {
  allIncidentSetBookmarkStatus,
  allIncidentsFetchData,
  defaultPageable,
} from '../../../actions/allIncidentsActions';
import {
  getAllIncidentsFilters,
  getAllIncidentsFiltersLevelStatusData,
  getAllIncidentsTableActionsBookmarkStatus,
  getAllIncidentsTableData,
  getAllIncidentsTableIsLoading,
} from '../../../reducers/reporting/allIncidents';
import AllIncidentsTableActions from './AllIncidentsTableActions';
import AllIncidentsTableItem from './AllIncidentsTableItem';
import {
  defaultSorting,
  getPaginationObject,
} from '../../../helpers/paginationHelper';
import { parentColumns } from './tableConfig';

const allIncidentsTableExpandedRowRender = reportType => data => (
  <AllIncidentsTableItem
    key={data.incidentId}
    data={data}
    reportType={reportType}
  />
);

const AllIncidentsTable = ({
  allIncidentSetBookmarkStatus,
  allIncidentsFetchData,
  filtersValues,
  incidentLevelStatusData,
  incidentsData,
  isLoading,
  reportType,
  tableActionsBookmarkStatus,
}) => {
  const [pagination, setPagination] = useState(getPaginationObject());

  useEffect(() => {
    if (incidentsData) {
      const current = get(incidentsData, 'number', 0) + 1;
      const pageSize = get(incidentsData, 'size', 0);
      const total = get(incidentsData, 'totalElements', 0);
      setPagination(getPaginationObject({ current, pageSize, total }));
    }
  }, [incidentsData]);

  const getData = useCallback(
    ({ pagination: { pageSize, current }, sorting: { columnKey, order } }) => {
      allIncidentsFetchData({
        filters: {
          ...filtersValues,
          pageable: paginationToPageable({
            current,
            sortProperty: columnKey,
            order,
            pageSize,
          }),
        },
        incidentLevelStatusData,
      });
    },
    [filtersValues, allIncidentsFetchData, incidentLevelStatusData]
  );

  const onTableChange = useCallback(
    (pagination, filters, newSorting) => {
      getData({
        pagination,
        sorting: isEmpty(newSorting)
          ? defaultSorting(defaultPageable())
          : newSorting,
      });
    },
    [getData]
  );

  const handleWatchingClick = useCallback(
    ({
      assetId,
      incident: {
        detectionLink: { detectionLinkId },
      },
      incidentId,
      watching,
    }) => () => {
      allIncidentSetBookmarkStatus({
        assetId,
        detectionLinkId,
        incidentId,
        watching: !watching,
      });
    },
    [allIncidentSetBookmarkStatus]
  );

  const content = get(incidentsData, 'content', null);

  if (content) {
    const buildParentColumns = parentColumns(
      {
        handleWatchingClick,
      },
      {
        tableActionsBookmarkStatus,
      },
      reportType
    );
    const buildExpandedRowRender = allIncidentsTableExpandedRowRender(
      reportType
    );
    return (
      <>
        <Table
          className="all-incidents-table"
          columns={buildParentColumns}
          dataSource={content}
          expandedRowRender={buildExpandedRowRender}
          loading={isLoading}
          onChange={onTableChange}
          pagination={pagination}
          rowKey={({ incidentId }) => incidentId}
          size="small"
        />
        <AllIncidentsTableActions reportType={reportType} />
      </>
    );
  }

  return null;
};

AllIncidentsTable.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  incidentsData: PropTypes.shape({
    content: PropTypes.arrayOf(PropTypes.object.isRequired),
    number: PropTypes.number.isRequired,
    size: PropTypes.number.isRequired,
    totalElements: PropTypes.number.isRequired,
  }),
  filtersValues: PropTypes.object.isRequired,
  allIncidentsFetchData: PropTypes.func.isRequired,
  allIncidentSetBookmarkStatus: PropTypes.func.isRequired,
  incidentLevelStatusData: PropTypes.shape({
    incidents: PropTypes.shape({
      error: PropTypes.object,
      isLoading: PropTypes.bool.isRequired,
      response: PropTypes.shape({
        data: PropTypes.arrayOf(PropTypes.object.isRequired),
        status: PropTypes.number,
      }),
    }),
    links: PropTypes.shape({
      error: PropTypes.object,
      isLoading: PropTypes.bool.isRequired,
      response: PropTypes.shape({
        data: PropTypes.arrayOf(PropTypes.object.isRequired),
        status: PropTypes.number,
      }),
    }),
  }),
  tableActionsBookmarkStatus: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  filtersValues: getAllIncidentsFilters(state),
  incidentLevelStatusData: getAllIncidentsFiltersLevelStatusData(state),
  incidentsData: getAllIncidentsTableData(state),
  isLoading: getAllIncidentsTableIsLoading(state),
  tableActionsBookmarkStatus: incidentId =>
    getAllIncidentsTableActionsBookmarkStatus(state, incidentId),
});

export default connect(mapStateToProps, {
  allIncidentSetBookmarkStatus,
  allIncidentsFetchData,
})(AllIncidentsTable);
